<template>
  <div
    class="plan-card"
    :class="{ recommended: plan.recommended, compact }"
  >
    <div
      v-if="plan.badge"
      class="plan-badge"
    >
      {{ plan.badge }}
    </div>

    <div class="plan-header">
      <h3>{{ plan.priceNickname || plan.name }}</h3>
      <p
        v-if="plan.priceNickname && plan.name"
        class="plan-subtitle"
      >
        {{ plan.name }}
      </p>
    </div>

    <div class="plan-price">
      <div class="price-line">
        <span class="price-text">{{ formatCurrency(plan.amount, plan.currency) }}</span>
      </div>
      <span class="period">{{ formatInterval(plan.interval, plan.intervalCount, plan.durationDays) }}</span>
    </div>

    <ul
      v-if="plan.features.length > 0"
      class="plan-features"
    >
      <li
        v-for="(feature, index) in plan.features"
        :key="index"
      >
        <span class="checkmark">✓</span> {{ feature }}
      </li>
    </ul>

    <button
      v-if="!disabled"
      class="btn-action"
      :class="{ 'btn-action-compact': compact }"
      :disabled="isProcessing"
      @click="emit('subscribe', plan.priceId)"
    >
      <span v-if="isProcessing">Processing...</span>
      <span v-else>{{ buttonText }}</span>
    </button>

    <div
      v-else
      class="disabled-notice"
    >
      <slot name="disabled-message">
        Not available
      </slot>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import type { StripePlan } from '../types';
import { formatInterval, formatCurrency } from '../utils/formatters';

interface Props {
  plan: StripePlan;
  isProcessing: boolean;
  disabled?: boolean;
  compact?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  disabled: false,
  compact: false,
});

const emit = defineEmits<{
  subscribe: [priceId: string];
}>();

const buttonText = computed(() => {
  return props.plan.mode === 'subscription' ? 'Subscribe Now' : 'Buy Now';
});
</script>

<style scoped>
.plan-card {
  border: 2px solid var(--color-border-primary);
  border-radius: var(--radius-xl);
  padding: var(--spacing-4xl);
  position: relative;
  transition: var(--transition-slow);
  background: var(--color-card-bg);
  display: flex;
  flex-direction: column;
}

.plan-card:hover {
  border-color: var(--brand-accent);
  box-shadow: var(--shadow-card-hover);
}

.plan-card.recommended {
  border-color: var(--brand-accent);
  box-shadow: var(--shadow-card);
}

.plan-card.compact {
  padding: var(--spacing-2xl);
}

.plan-badge {
  position: absolute;
  top: 0;
  right: var(--spacing-xl);
  transform: translateY(-50%);
  background: var(--gradient-accent);
  color: var(--color-text-inverse);
  padding: var(--spacing-xs) var(--spacing-lg);
  border-radius: var(--radius-full);
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-bold);
  text-transform: uppercase;
  letter-spacing: var(--letter-spacing-wide);
  box-shadow: var(--shadow-md);
  border: 2px solid var(--color-card-bg);
}

.plan-card:not(.recommended) .plan-badge {
  background: var(--color-bg-secondary);
  color: var(--color-text-primary);
  border: 2px solid var(--color-card-bg);
}

.plan-header {
  margin-bottom: var(--spacing-2xl);
}

.plan-header h3 {
  font-size: var(--font-size-2xl);
  margin-bottom: var(--spacing-sm);
  color: var(--color-text-primary);
}

.plan-card.compact .plan-header h3 {
  font-size: var(--font-size-xl);
}

.plan-subtitle {
  color: var(--color-text-tertiary);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  text-transform: uppercase;
  letter-spacing: var(--letter-spacing-wide);
  margin: var(--spacing-xs) 0 0 0;
}

.plan-card.compact .plan-subtitle {
  font-size: var(--font-size-xs);
}

.plan-description {
  color: var(--color-text-secondary);
  font-size: var(--font-size-base);
  line-height: var(--line-height-relaxed);
  min-height: 2.5em;
}

.plan-card.compact .plan-description {
  font-size: var(--font-size-sm);
  min-height: 2em;
}

.plan-price {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  margin-bottom: var(--spacing-2xl);
  padding-bottom: var(--spacing-2xl);
  border-bottom: 1px solid var(--color-border);
}

.price-line {
  display: flex;
  align-items: baseline;
}

.price-text {
  font-size: var(--font-size-4xl);
  font-weight: var(--font-weight-bold);
  color: var(--color-text-primary);
}

.plan-card.compact .price-text {
  font-size: var(--font-size-3xl);
}

.period {
  font-size: var(--font-size-lg);
  color: var(--color-text-secondary);
  margin-top: var(--spacing-xs);
}

.plan-card.compact .period {
  font-size: var(--font-size-base);
}

.plan-features {
  list-style: none;
  padding: 0;
  margin: 0 0 var(--spacing-3xl) 0;
  flex: 1;
}

.plan-features li {
  padding: var(--spacing-sm) 0;
  color: var(--color-text-secondary);
  display: flex;
  align-items: center;
  gap: var(--spacing-md);
  font-size: var(--font-size-base);
}

.plan-card.compact .plan-features li {
  font-size: var(--font-size-sm);
  padding: var(--spacing-xs) 0;
}

.checkmark {
  color: var(--brand-success);
  font-weight: var(--font-weight-bold);
  font-size: var(--font-size-xl);
}

/* Action button now uses global .btn-action utility */
.btn-action {
  width: 100%;
  padding: var(--spacing-lg) var(--spacing-2xl);
  font-size: var(--font-size-lg);
  border-radius: var(--radius-lg);
  margin-top: auto;
}

.btn-action-compact {
  padding: var(--spacing-md) var(--spacing-2xl);
  font-size: var(--font-size-base);
}

.disabled-notice {
  text-align: center;
  padding: var(--spacing-lg);
  background: var(--color-bg-secondary);
  border-radius: var(--radius-lg);
  color: var(--color-text-secondary);
  font-size: var(--font-size-sm);
  font-style: italic;
}
</style>
